// path: frontend/src/components/pages/admin/models/ModelsModal.jsx
import React, { useState, useEffect } from "react";
import { getAllModelTypes, getAllSources, getAllModels } from "../../../../api/apiFunction/modelServices";

const ModelsModal = ({ isOpen, model, onSave, onClose }) => {
  const [form, setForm] = useState(
    model || {
      name: "",
      title: "",
      provider: "",
      type: "free",
      active: true,
      modelType: "text",
      source: "",
      llmClass: "low",
      support: { imageIn: false, imageOut: false, pdf: false, search: false },
      isAuto: false,
      enhancePrompt: false,
    }
  );

  const [modelTypeOptions, setModelTypeOptions] = useState(["text", "image", "image-edit", "animation"]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchDropdownData = async () => {
      try {
        const [typesRes, sourcesRes, modelsRes] = await Promise.all([
          getAllModelTypes().catch(() => ({ data: [] })),
          getAllSources().catch(() => ({ data: [] })),
          getAllModels().catch(() => ({ data: [] })),
        ]);

        // Model Types
        const typeFromSupports = Array.from(
          new Set(
            (typesRes.data || []).flatMap((t) =>
              Array.isArray(t.supports) ? t.supports : []
            )
          )
        );
        const typeFromTypeField = Array.from(
          new Set((typesRes.data || []).map((t) => t.type).filter(Boolean))
        );
        const mergedTypes = Array.from(
          new Set([
            ...(typeFromSupports.length ? typeFromSupports : []),
            ...typeFromTypeField,
          ])
        );
        if (mergedTypes.length) setModelTypeOptions(mergedTypes);

        // Sources (capitalize first letter)
        const sources = (sourcesRes.data || [])
          .map((s) => s.name)
          .filter(Boolean)
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1));
        if (sources.length) setSourceOptions(sources);

        // Providers
        const providers = Array.from(
          new Set((modelsRes.data || []).map((m) => m.provider).filter(Boolean))
        );
        if (providers.length) setProviderOptions(providers);
      } catch (_) {
        // keep defaults on failure
      }
    };
    fetchDropdownData();
  }, [isOpen]);

  useEffect(() => {
    if (model) {
      setForm({
        name: model.name || "",
        title: model.title || "",
        provider: model.provider || "",
        type: model.type || "free",
        active: model.active !== undefined ? model.active : true,
        modelType: model.modelType || "text",
        source: model.source || "",
        llmClass: model.llmClass || "low",
        support: {
          imageIn: model.support?.imageIn || false,
          imageOut: model.support?.imageOut || false,
          pdf: model.support?.pdf || false,
          search: model.support?.search || false,
        },
        isAuto: model.isAuto || false,
        enhancePrompt: model.enhancePrompt || false,
        _id: model._id,
      });
    }
  }, [model]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("support.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({
        ...prev,
        support: {
          ...prev.support,
          [key]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-3xl relative">
        <button
          className="absolute top-2 right-2 text-fg-60 hover:text-fg-50 text-2xl"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-lg font-bold mb-6">Edit Model</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Name
            </label>
            <input
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Title
            </label>
            <input
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="title"
              value={form.title}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Provider
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="provider"
              value={form.provider}
              onChange={handleChange}
              required
            >
              {providerOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Type
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="type"
              value={form.type}
              onChange={handleChange}
            >
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              LLM Class
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="llmClass"
              value={form.llmClass}
              onChange={handleChange}
            >
              <option value="low">Low</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Model Type
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="modelType"
              value={form.modelType}
              onChange={handleChange}
            >
              {modelTypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Source
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="source"
              value={form.source}
              onChange={handleChange}
            >
              {sourceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Status
            </label>
            <select
              className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
              name="active"
              value={form.active}
              onChange={handleChange}
            >
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-fg-60 mb-1">
              <input
                type="checkbox"
                name="isAuto"
                checked={form.isAuto || false}
                onChange={handleChange}
                className="w-4 h-4 rounded"
              />
              Set as Auto Model
            </label>
            <p className="text-xs text-fg-60 mt-1">
              When enabled, this model will be used for "Auto" selection and web search. Only one model can be auto at a time.
            </p>
          </div>

          {/* Enhance Prompt - only for image generation models */}
          {(String(form.modelType).toLowerCase() === "image" ||
            String(form.modelType).toLowerCase() === "image-edit") && (
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-xs font-medium text-fg-60 mb-1">
                <input
                  type="checkbox"
                  name="enhancePrompt"
                  checked={!!form.enhancePrompt}
                  onChange={handleChange}
                  className="w-4 h-4 rounded"
                />
                Auto enhance / translate prompts to English
              </label>
              <p className="text-xs text-fg-60 mt-1">
                When enabled, image prompts written in any language will be optimized and converted to English before sending to the model.
              </p>
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-xs font-medium text-fg-60 mb-1">
              Support
            </label>
            <div className="flex gap-4 flex-wrap">
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  name="support.imageIn"
                  checked={form.support.imageIn}
                  onChange={handleChange}
                />{" "}
                Image In
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  name="support.imageOut"
                  checked={form.support.imageOut}
                  onChange={handleChange}
                />{" "}
                Image Out
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  name="support.pdf"
                  checked={form.support.pdf}
                  onChange={handleChange}
                />{" "}
                PDF
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  name="support.search"
                  checked={form.support.search}
                  onChange={handleChange}
                />{" "}
                Search
              </label>
            </div>
          </div>

          <div className="col-span-2 flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="px-3 py-1.5 text-xs rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs rounded bg-ac-02 text-white dark:text-black font-semibold hover:bg-ac-01"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModelsModal;
